<script>
	/**
	 * [디자인] 탭 — 브랜드 로고 + 테마(색·테두리·모서리·글꼴).
	 * 설계: project-le868/launcher-page-design.md §2-1 · §2-3
	 *
	 * ⭐ **메인(홈) 화면 저작은 [화면] 탭에 있다**(ScreenContext '메인 화면' 섹션).
	 *    배경·마스코트·말풍선·상단 바가 여기 있었는데, 그건 화면이라 다른 화면들과 나란히
	 *    있어야 프리뷰로 보면서 만들 수 있다 — [화면] 탭만 프리뷰 화면을 가리킨다.
	 *    저장 칸은 그대로 SHELL 이다(탭만 옮겼다).
	 * ⚠️ 이 탭이 만지는 건 서버 SHELL(로고) / THEME 두 칸이다. 저장은 **바뀐 칸만** 올린다
	 *    (전체를 매번 보내면 다른 탭의 최신 저장을 낡은 값으로 덮는다).
	 */
	import { createEventDispatcher } from "svelte";
	import { Section, Label } from "../../lib/ui";
	import ImagePicker from "../chat-builder/ImagePicker.svelte";
	import { botAssetFolder } from "../chat-builder/imageAssetApi.js";
	import {
		BRAND_SLOTS,
		THEME_COLORS,
		DEFAULT_RADIUS,
		BORDER_STYLES,
		DEFAULT_BORDER_STYLE,
		THEME_PRESETS,
		DEFAULT_TEXT_SCALE,
		TEXT_SCALE_MIN,
		TEXT_SCALE_MAX,
		LAUNCHER_FONTS,
	} from "./launcherSlots.js";
	import { brushSample } from "./brushBorder.js";

	export let bot;
	/** 서버 SHELL 칸. 여기서는 `assets.logo` 만 건드린다([화면] 탭과 **같은 칸**이다). */
	export let shell = {};
	/** 서버 THEME 칸. { colors:{...}, radius, textScale, fontFamily, borderStyle } */
	export let theme = {};
	/** 슬롯키 → 현재 버전 URL. 썸네일용(저장하지 않는다). */
	export let urls = {};

	const dispatch = createEventDispatcher();

	// 서버는 **저작자가 정한 값만** 내려준다 → 화면에 그릴 기본값은 여기서 채운다.
	// ⚠️⚠️ 원본(shell)을 먼저 펼친다 — [화면] 탭이 쓰는 statBar·talkBoard·screens 와 **같은 칸**이라
	//    여기서 재조립하면 그쪽 저작이 조용히 날아간다.
	$: nShell = {
		...(shell || {}),
		assets: { ...(shell?.assets || {}) },
	};
	$: nTheme = {
		...(theme || {}),
		radius: theme?.radius ?? DEFAULT_RADIUS,
		textScale: theme?.textScale ?? DEFAULT_TEXT_SCALE,
		borderStyle: theme?.borderStyle || DEFAULT_BORDER_STYLE,
		colors: { ...(theme?.colors || {}) },
	};

	const emitShell = (patch) => dispatch("shell", { ...nShell, ...patch });
	const emitTheme = (patch) => dispatch("theme", { ...nTheme, ...patch });

	const setColor = (key, value) => emitTheme({ colors: { ...nTheme.colors, [key]: value } });
	/**
	 * 색 칸에 보일 값. 비어 있으면 — `follows` 칸은 **따라가는 칸의 지금 값**(런타임이 실제로 그 색을 쓴다),
	 * 아니면 고정 기본값. ⚠️ colors 를 인자로 받는다 — Svelte 가 마크업에서 그 값이 바뀔 때 다시 부르게.
	 */
	const colorValue = (c, colors) =>
		colors[c.key] ||
		(c.follows ? colorValue(THEME_COLORS.find((x) => x.key === c.follows), colors) : c.fallback);

	/**
	 * 지금 테마가 그 묶음과 **같은지**. 고른 묶음에 표시를 남기려고 쓴다.
	 * ⚠️ 글꼴·글자 크기는 비교하지 않는다 — 묶음이 그 둘을 건드리지 않으므로(launcherSlots 주석)
	 *    달라도 "그 묶음을 쓰는 중"이 맞다.
	 */
	const isPreset = (preset) =>
		preset.theme.borderStyle === nTheme.borderStyle &&
		preset.theme.radius === nTheme.radius &&
		Object.entries(preset.theme.colors).every(([k, v]) => nTheme.colors[k] === v);

	/** 묶음 적용. colors 를 통째로 갈아 끼운다(낱개로 칠한 값이 섞이면 묶음이 아니게 된다). */
	const applyPreset = (preset) => emitTheme({ ...preset.theme, colors: { ...preset.theme.colors } });

	/** 묶음 미리보기 점. 역할이 드러나는 순서로(바탕→면→강조→글자→테두리). */
	const DOT_KEYS = ["background", "surface", "primary", "text", "border"];

	/* ── 그림 슬롯 ──────────────────────────────────────────────────────── */
	let pickerOpen = false;
	let pickingSlot = null;
	$: currentPickerAssetId = pickingSlot ? nShell.assets[pickingSlot] || null : null;

	const openPicker = (slot) => {
		pickingSlot = slot;
		pickerOpen = true;
	};
	const onPick = (e) => {
		if (!pickingSlot) return;
		const { assetId, url } = e.detail || {};
		// 썸네일은 즉시 반영하고(저장 왕복을 기다리지 않게), 저장은 assetId 만 올린다.
		urls = { ...urls, [pickingSlot]: url };
		emitShell({ assets: { ...nShell.assets, [pickingSlot]: assetId } });
		pickingSlot = null;
	};
	const clearSlot = (slot) => {
		const assets = { ...nShell.assets };
		delete assets[slot];
		const next = { ...urls };
		delete next[slot];
		urls = next;
		emitShell({ assets });
	};
</script>

<div class="tw-flex tw-flex-col tw-gap-6">
	<!-- ⭐ 화면에 쓰는 그림(배경·마스코트)은 [화면] 탭 '메인 화면'으로 옮겼다 — 프리뷰로 보면서
	     만들어야 하는 건 화면이고, 여기 남는 건 화면과 무관한 브랜드 자산(로고)뿐이다. -->
	<Section title="로고" description="런처의 브랜드 그림입니다.">
		<div class="tw-flex tw-flex-col tw-gap-4">
			{#each BRAND_SLOTS as slot (slot.key)}
				<div class="tw-flex tw-items-start tw-gap-3">
					<button
						type="button"
						class="slot-thumb tw-shrink-0"
						on:click={() => openPicker(slot.key)}
						title="{slot.label} 고르기"
					>
						{#if urls[slot.key]}
							<img src={urls[slot.key]} alt="" />
						{:else}
							<span class="tw-text-[11px] tw-text-muted-foreground">고르기</span>
						{/if}
					</button>
					<div class="tw-min-w-0 tw-flex-1">
						<div class="tw-text-sm tw-font-medium">{slot.label}</div>
						<p class="tw-mt-0.5 tw-text-xs tw-text-muted-foreground">{slot.hint}</p>
						{#if nShell.assets[slot.key]}
							<button
								type="button"
								class="tw-mt-1 tw-text-xs tw-text-muted-foreground tw-underline"
								on:click={() => clearSlot(slot.key)}>비우기</button
							>
						{/if}
					</div>
				</div>
			{/each}
		</div>
	</Section>

	<Section
		title="테마"
		description="여기서 정한 색과 모양이 런처 화면 전체에 쓰입니다. 나중에 채팅·게임으로도 전달됩니다."
	>
		<div class="tw-flex tw-flex-col tw-gap-4">
			<!-- ⭐ 묶음을 먼저, 낱개를 나중에. 저작자는 보통 "비슷한 걸 고르고 조금 고친다". -->
			<div class="tw-flex tw-flex-col tw-gap-2">
				<Label class="tw-text-sm">묶음으로 시작</Label>
				<div class="tw-grid tw-grid-cols-2 tw-gap-2">
					{#each THEME_PRESETS as p (p.key)}
						<button
							type="button"
							class="bd-tile"
							class:bd-on={isPreset(p)}
							on:click={() => applyPreset(p)}
						>
							<span class="pre-dots">
								{#each DOT_KEYS as k}
									<i style="background:{p.theme.colors[k]}"></i>
								{/each}
							</span>
							<span class="bd-label">{p.label}</span>
							<span class="bd-hint">{p.sample}</span>
						</button>
					{/each}
				</div>
			</div>
			<div class="tw-grid tw-grid-cols-2 tw-gap-3 sm:tw-grid-cols-3">
				{#each THEME_COLORS as c (c.key)}
					<div class="tw-flex tw-items-center tw-gap-2">
						<input
							type="color"
							class="tw-h-8 tw-w-8 tw-shrink-0 tw-cursor-pointer tw-rounded tw-border tw-border-border tw-bg-transparent tw-p-0"
							value={colorValue(c, nTheme.colors)}
							on:input={(e) => setColor(c.key, e.target.value)}
						/>
						<span class="tw-truncate tw-text-xs" title={c.follows && !nTheme.colors[c.key] ? "비워 두면 다른 색을 따라갑니다" : ""}
							>{c.label}</span
						>
					</div>
				{/each}
			</div>
			<!-- ⭐ 테두리 '획' — 색과 따로 둔다. 색 토큰 아래, 모서리 위가 제자리다(둘 다 모양 축).
			     ⚠️ 샘플은 **런타임과 같은 붓·같은 찍기**다(brushBorder.js = 런타임 brushBorder.ts 한 벌).
			        시안 붓 원본을 경로 따라 찍어 border-image 로 붙인다 — 근사 그림이 아니라 실제와 같은 결이다. -->
			<div class="tw-flex tw-flex-col tw-gap-2">
				<Label class="tw-text-sm">테두리</Label>
				<div class="tw-grid tw-grid-cols-2 tw-gap-2 sm:tw-grid-cols-4">
					{#each BORDER_STYLES as b (b.value)}
						<button
							type="button"
							class="bd-tile"
							class:bd-on={nTheme.borderStyle === b.value}
							on:click={() => emitTheme({ borderStyle: b.value })}
						>
							<!-- 굵기 = 런타임 카드와 같다(붓 3px · 손그림 선 2px). 붓은 brushSample 이 찍는다. -->
							<span
								class="bd-sample"
								style={b.value === "brushStraight" || b.value === "brush" ? "border-width:3px" : "border-width:2px"}
								use:brushSample={b.value}
							></span>
							<span class="bd-label">{b.label}</span>
							<span class="bd-hint">{b.hint}</span>
						</button>
					{/each}
				</div>
			</div>
			<div class="tw-flex tw-items-center tw-gap-3">
				<Label class="tw-w-20 tw-shrink-0 tw-text-sm">모서리</Label>
				<input
					type="range"
					min="0"
					max="32"
					step="2"
					class="tw-flex-1"
					value={nTheme.radius}
					on:input={(e) => emitTheme({ radius: Number(e.target.value) })}
				/>
				<span class="tw-w-10 tw-text-right tw-text-xs tw-text-muted-foreground">{nTheme.radius}px</span>
			</div>
			<!-- ⭐ 글자 크기는 **배율 하나**다(낱개 px 이 아니다). 말풍선·이름표·타일 문구가 서로
			     비율을 맞춰야 한 화면으로 읽히는데, 낱개로 열어 주면 이름표(20px)와 말풍선(15px)이
			     어긋나 있던 일이 저작자 손에서 다시 일어난다.
			     ⚠️ 글꼴 바로 위가 제자리다 — 손글씨 글꼴은 같은 px 에서도 작아 보여서 **둘을 같이**
			        만지게 된다. -->
			<div class="tw-flex tw-items-center tw-gap-3">
				<Label class="tw-w-20 tw-shrink-0 tw-text-sm">글자 크기</Label>
				<input
					type="range"
					min={TEXT_SCALE_MIN}
					max={TEXT_SCALE_MAX}
					step="0.05"
					class="tw-flex-1"
					value={nTheme.textScale}
					on:input={(e) => emitTheme({ textScale: Number(e.target.value) })}
				/>
				<span class="tw-w-10 tw-text-right tw-text-xs tw-text-muted-foreground"
					>{Math.round(nTheme.textScale * 100)}%</span
				>
			</div>
			<p class="tw-text-xs tw-text-muted-foreground">
				말풍선·이름표·버튼 문구처럼 <strong>저작자가 쓴 글</strong>이 함께 커집니다. 상단 바와 안내
				문구는 칸 높이가 정해져 있어 따라가지 않습니다.
			</p>
			<!-- ⭐ 자유 입력이었는데 목록으로 바꿨다 — 서버 FONT_FILES 에 없는 이름을 적으면
			     셸에 @font-face 가 안 박혀 **조용히 안 먹는다**(저작자는 이유를 알 수 없다).
			     샘플은 그 글꼴로 직접 보여준다(봇 빌더 [디자인] 탭과 같은 방식). -->
			<div class="tw-flex tw-flex-col tw-gap-2">
				<Label class="tw-text-sm">글꼴</Label>
				<div class="tw-grid tw-grid-cols-2 tw-gap-2 sm:tw-grid-cols-4">
					{#each LAUNCHER_FONTS as f (f.key)}
						<button
							type="button"
							class="bd-tile"
							class:bd-on={(nTheme.fontFamily || "") === f.key}
							on:click={() => emitTheme({ fontFamily: f.key })}
						>
							<span class="font-sample" style="font-family:'{f.key}'">{f.sample}</span>
							<span class="bd-label">{f.label}</span>
						</button>
					{/each}
				</div>
			</div>
		</div>
	</Section>
</div>

<ImagePicker
	bind:open={pickerOpen}
	chatId={bot.id}
	contextFolder={botAssetFolder(bot)}
	mode="single"
	currentAssetId={currentPickerAssetId}
	on:select={onPick}
/>

<style>
	/* ── 테두리 방식 고르기 ────────────────────────────────────────────────
	   말로는 차이가 안 와닿는다 — 그 방식으로 그린 사각형을 직접 보여준다. */
	.bd-tile {
		position: relative;
		display: flex;
		flex-direction: column;
		align-items: center;
		gap: 4px;
		padding: 10px 8px;
		border: 1px solid hsl(var(--sb-border));
		border-radius: 10px;
		background: hsl(var(--sb-background));
		cursor: pointer;
		text-align: center;
	}
	.bd-tile:hover {
		background: hsl(var(--sb-muted) / 0.4);
	}
	/* ⚠️ 1px 테두리만으로는 **고른 게 뭔지 안 보였다.** 테두리·바탕·글자·체크 넷을 같이 바꾼다.
	   ::after 로 체크를 그려서 타일 세 종류(묶음·테두리·글꼴)가 마크업 없이 같이 적용된다. */
	.bd-on {
		border-color: hsl(var(--sb-primary));
		background: hsl(var(--sb-primary) / 0.08);
		box-shadow: 0 0 0 2px hsl(var(--sb-primary) / 0.3);
	}
	.bd-on .bd-label {
		color: hsl(var(--sb-primary));
	}
	.bd-on::after {
		content: "✓";
		position: absolute;
		top: 3px;
		right: 6px;
		font-size: 11px;
		font-weight: 700;
		line-height: 1;
		color: hsl(var(--sb-primary));
	}
	/* 샘플 네모. **런타임과 같은 방식**이다 — 테두리를 가진 상자에 찍은 붓을 border-image 로 붙인다.
	   ⚠️ 붓 획 가운데가 상자 경계라 반은 바깥으로 나간다(런타임과 같다) — 타일 여백 안에 들어온다. */
	.bd-sample {
		width: 100%;
		height: 34px;
		border-style: solid;
		border-color: currentColor;
		border-radius: 10px;
		color: hsl(var(--sb-foreground));
	}
	/* 묶음 미리보기 점 — 색 다섯 개를 겹치지 않게 나란히. */
	.pre-dots {
		display: flex;
		gap: 3px;
		height: 18px;
		align-items: center;
	}
	.pre-dots i {
		width: 14px;
		height: 14px;
		border-radius: 999px;
		border: 1px solid hsl(var(--sb-border));
	}
	/* 글꼴 샘플 — 그 글꼴로 보여주지 않으면 이름만으로는 고를 수 없다. */
	.font-sample {
		font-size: 17px;
		line-height: 1.4;
		height: 26px;
		overflow: hidden;
		white-space: nowrap;
		color: hsl(var(--sb-foreground));
	}
	.bd-label {
		font-size: 12px;
		font-weight: 600;
	}
	.bd-hint {
		font-size: 10px;
		line-height: 1.3;
		color: hsl(var(--sb-muted-foreground));
	}

	/* 투명 PNG 가 많아 체크무늬 바탕에 올려야 가장자리가 보인다. */
	.slot-thumb {
		width: 88px;
		height: 88px;
		display: flex;
		align-items: center;
		justify-content: center;
		overflow: hidden;
		border-radius: 10px;
		border: 1px dashed hsl(var(--sb-border));
		background-color: hsl(var(--sb-muted));
		background-image:
			linear-gradient(45deg, rgba(0, 0, 0, 0.06) 25%, transparent 25%),
			linear-gradient(-45deg, rgba(0, 0, 0, 0.06) 25%, transparent 25%),
			linear-gradient(45deg, transparent 75%, rgba(0, 0, 0, 0.06) 75%),
			linear-gradient(-45deg, transparent 75%, rgba(0, 0, 0, 0.06) 75%);
		background-size: 12px 12px;
		background-position: 0 0, 0 6px, 6px -6px, -6px 0;
		cursor: pointer;
	}
	.slot-thumb img {
		max-width: 100%;
		max-height: 100%;
		object-fit: contain;
	}
</style>
